/**
 * WebSocket hook для realtime событий сообщений (TODO-018b)
 * Дата: 15.01.2026
 *
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - `frontend/src/contexts/WebSocketContext.tsx` — использует этот hook и обновляет React Query кеш
 * - `frontend/src/pages/Messages.tsx` — показывает ConnectionIndicator / toast и передаёт tenantId
 * - Backend: `cmd/api/main.go` + `internal/handler/websocket.go` — WS endpoint `/ws/messages`
 *
 * КАК РАБОТАЕТ (кратко):
 * - Подключается к `/ws/messages?tenant_id=...`
 * - Парсит JSON события `{ type, payload }`
 * - Делает авто-reconnect с backoff
 *
 * СХЕМА:
 * - Композиция функций:
 *   - useMessagesWebSocket -> connect() -> WebSocket handlers (onopen/onmessage/onclose)
 * - Схема вызовов:
 *   - mount -> connect -> (event -> onEvent) -> close -> reconnect
 * - Использование из других модулей:
 *   - только через `WebSocketProvider` (контекст)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '@/store/authStore';

export type WebSocketConnectionStatus = 'connected' | 'reconnecting' | 'offline';

export interface MessagesWsEvent<TPayload = unknown> {
  type: string;
  payload: TPayload;
}

export interface NewMessagePayload {
  id: string;
  phone: string;
  phone_normalized: string;
  channel: string;
  direction: 'incoming' | 'outgoing' | string;
  content: string;
  sender_name?: string;
  sent_at: string; // ISO
}

export interface MessageStatusPayload {
  id: string;
  status: string;
  updated_at: string; // ISO
}

interface UseMessagesWebSocketParams {
  tenantId?: string;
  enabled?: boolean;
  onEvent?: (event: MessagesWsEvent) => void;
}

// Дата: 15.01.2026 — добавлен token для WS auth (браузер не может передать header)
function buildWsUrl(tenantId: string, token: string | null) {
  const apiBase = (import.meta as any).env?.VITE_API_URL || '/api';
  const apiUrl = new URL(apiBase, window.location.origin);
  const wsUrl = new URL('/ws/messages', apiUrl);
  wsUrl.protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  wsUrl.searchParams.set('tenant_id', tenantId);
  if (token) {
    wsUrl.searchParams.set('token', token);
  }
  return wsUrl.toString();
}

export function useMessagesWebSocket({ tenantId, enabled = true, onEvent }: UseMessagesWebSocketParams) {
  const [status, setStatus] = useState<WebSocketConnectionStatus>('offline');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const onEventRef = useRef<UseMessagesWebSocketParams['onEvent']>(onEvent);
  const attemptRef = useRef(0);
  
  // Дата: 15.01.2026 — получаем token для WS auth
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const url = useMemo(() => {
    if (!tenantId) return null;
    return buildWsUrl(tenantId, token);
  }, [tenantId, token]);

  useEffect(() => {
    if (!enabled || !tenantId || !url) return;
    if (typeof WebSocket === 'undefined') return;

    let disposed = false;

    const clearReconnect = () => {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const cleanupSocket = () => {
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        try {
          ws.close();
        } catch {
          // ignore
        }
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      clearReconnect();
      attemptRef.current += 1;
      const delay = Math.min(30_000, 500 * Math.pow(2, attemptRef.current)); // 0.5s -> 1s -> 2s ... max 30s
      setStatus('reconnecting');
      reconnectTimerRef.current = window.setTimeout(() => {
        connect();
      }, delay);
    };

    const connect = () => {
      if (disposed) return;
      cleanupSocket();

      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          attemptRef.current = 0;
          setStatus('connected');
        };

        ws.onmessage = (ev) => {
          try {
            const parsed = JSON.parse(String(ev.data)) as MessagesWsEvent;
            onEventRef.current?.(parsed);
          } catch {
            // ignore invalid payloads
          }
        };

        ws.onerror = () => {
          // close will trigger reconnect
          try {
            ws.close();
          } catch {
            // ignore
          }
        };

        ws.onclose = () => {
          if (disposed) return;
          setStatus('offline');
          scheduleReconnect();
        };
      } catch {
        setStatus('offline');
        scheduleReconnect();
      }
    };

    setStatus('reconnecting');
    connect();

    return () => {
      disposed = true;
      clearReconnect();
      cleanupSocket();
      setStatus('offline');
    };
  }, [enabled, tenantId, url]);

  return { status };
}

